import { useState } from "react";
import { Menu, menuClasses, MenuItem, MenuProps } from "@kaltura/ds-react-components";
import styled from "@emotion/styled";
import {
    AddNewMenuModalInjectedProps,
    ButtonClickAnalyticsType,
    KmsTypeAddNewMenuItem,
    KmsTypeAddNewMenuSection,
    KmsTypeComponent,
} from "@kaltura/mediaspace-shared-types";
import { AddNewIllustration } from "./AddNewIllustration";
import { translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { isMobile } from "react-device-detect";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { CustomComponent } from "@kaltura/mediaspace-shared-ui";

export interface AddNewMenuProps extends MenuProps {
    sections: KmsTypeAddNewMenuSection[];
    hideIllustration?: boolean;
    /**
     * set menu minHeight to auto so menu height will fit menu items content - default is 400px
     * used in KAF
     */
    autoMinHeight?: boolean;
    /**
     * set menu item left and right margins to 8px - default is 16px
     * used in KAF
     */
    narrowMargin?: boolean;
    /**
     * Function to close the menu
     */
    closeMenu?: () => void;
}

const columnWidth = 280;
const menuItemWidth = columnWidth - 32;

const StyledMenu = styled(
    Menu,
    {
        shouldForwardProp(propName: string) {
            return (
                propName !== "shortScreen"
                && propName !== "veryShortScreen"
                && propName !== "hideIllustration"
                && propName !== "autoMinHeight"
            );
        },
    }
)<{
    shortScreen: boolean;
    veryShortScreen: boolean;
    hideIllustration?: boolean;
    autoMinHeight?: boolean;
}>(({ theme, shortScreen, veryShortScreen, hideIllustration, autoMinHeight }) => ({
    [`.${menuClasses.paper}`]: {
        // override `backgroundColor` due to dark-mode design
        backgroundColor: theme.kaltura.palette.surfaces.background,
        borderRadius: theme.kaltura.shape.roundness2,
        minWidth: hideIllustration ? menuItemWidth : columnWidth * 2,
        maxHeight: shortScreen ? "100vh" : 562,
        minHeight: autoMinHeight ? "auto" : veryShortScreen ? "100vh" : 400,
        // menu is vertically aligned when menu items reach bottom screen edge by MUI
        // in KMS the menu is rendered at the top of the page header thus 20px are added to align with it
        // in KAF (autoMinHeight=true) the menu is rendered at the bottom of my-media header thus it is not needed
        marginTop: shortScreen && !autoMinHeight ? 20 : "",
        [theme.breakpoints.down("md")]: {
            minWidth: menuItemWidth,
        },
        [`.${menuClasses.list}`]: {
            // mui calculates the browser's scrollbar width and updates its menu width accordingly
            // this change of width is not desirable in our case
            // !important prevents mui's inline calculation from affecting the css
            width: "100% !important",
            paddingRight: "0px !important",
        },
    },
}));

const StyledMenuItem = styled(
    MenuItem<"a">,
    {
        shouldForwardProp(propName) {
            return propName !== "narrowMargin";
        },
    }
)<{ narrowMargin?: boolean }>(({ theme, narrowMargin }) => ({
    height: 48,
    marginLeft: theme.spacing(narrowMargin ? 1 : 2),
    marginRight: theme.spacing(narrowMargin ? 1 : 2),
    marginTop: 0,
    marginBottom: 0,
    maxWidth: menuItemWidth,
    [`& > div`]: {
        height: 48,
        [`& > div`]: {
            display: "flex",
            alignItems: "center",
            fontWeight: theme.kaltura.typography.fontWeightRegular,
            fontSize: theme.typography.pxToRem(16),
            textTransform: "capitalize",
            color: theme.kaltura.palette.tone1,
            textDecoration: "none",
            verticalAlign: "middle",
            "&:hover": {
                color: theme.kaltura.palette.tone1,
                fontWeight: theme.kaltura.typography.fontWeightRegular,
            },
            "&:visited": {
                color: theme.kaltura.palette.tone1,
            },
        },
    },
    [`&:hover > div, &:focus > div`]: {
        backgroundColor: theme.kaltura.palette.surfaces.paper,
        borderRadius: theme.kaltura.shape.roundness1,
    },
    // override bootstrap a:focus textDecoration loaded on non-DS pages (KAF my-media)
    // override kms a:focus outline loaded on non-DS pages (KAF my-media)
    "&:focus": {
        textDecoration: "none",
        outline: "none",
    },
    [theme.breakpoints.down("md")]: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
}));

const StyledIcon = styled("img")(({ theme }) => ({
    width: 32,
    height: 32,
    marginRight: theme.spacing(1),
    verticalAlign: "middle",
}));

const StyledSectionTitle = styled("li")(({ theme }) => ({
    margin: theme.spacing(2, 3, 1, 3),
    maxWidth: menuItemWidth,
    textTransform: "uppercase",
    fontSize: theme.typography.pxToRem(13),
    color: theme.kaltura.palette.tone2,
    overflow: "hidden",
    textOverflow: "ellipsis",
}));

/**
 * `Add New` menu
 */
export function AddNewMenu(props: AddNewMenuProps) {
    const { sections, hideIllustration, autoMinHeight = false, narrowMargin = false, closeMenu, ...rest } = props;
    const [currentItem, setCurrentItem] = useState(sections[0].items[0]);
    // Tracks open modal; key increments to force fresh render
    const [openModal, setOpenModal] = useState<{
        component: KmsTypeComponent<any, AddNewMenuModalInjectedProps>;
        key: number;
    } | null>(null);

    const shortScreen = useMediaQuery(`(max-height:562px)`);
    const veryShortScreen = useMediaQuery(`(max-height:400px)`);

    const sendButtonAnalytics = useButtonAnalytics();

    const handleMenuItemClick = (item: KmsTypeAddNewMenuItem) => {
        const modalComponent = item.modalComponent;
        if (modalComponent) {
            closeMenu?.();
            // Set modal to open with incremented key (forces new component instance)
            setOpenModal((prev) => ({
                component: modalComponent,
                key: (prev?.key ?? 0) + 1,
            }));
        }
        if (item.analyticsValue) {
            sendButtonAnalytics(`Add - ${item.analyticsValue}`, ButtonClickAnalyticsType.CHOOSE);
        }
    };

    const getDesktopOnlyMenuItems = (sections: KmsTypeAddNewMenuSection[]) =>
        sections.flatMap((section) => section.items.filter((item) => !isMobile || !item.hideOnMobile));

    // flatten section label and items to items array
    const menItems = sections.map((section: KmsTypeAddNewMenuSection, sectionIndex: number) => {
        // add section items
        const items = getDesktopOnlyMenuItems([section]).map((item: KmsTypeAddNewMenuItem, index: number) => {
            return (
                <StyledMenuItem
                    key={sectionIndex + "-" + index}
                    onFocus={() => setCurrentItem(item)}
                    onMouseEnter={() => setCurrentItem(item)}
                    component="a"
                    href={item.modalComponent ? undefined : item.url}
                    aria-label={translate("%1 menuitem %2 %3", [item.label, item.description, section?.label])}
                    onClick={() => {
                        handleMenuItemClick(item);
                    }}
                    narrowMargin={narrowMargin}
                    className={"kms-ds-add-new-menu-item"}
                >
                    <StyledIcon src={item.iconUrl} alt="" />
                    {item.label}
                </StyledMenuItem>
            );
        });

        // add section label if we have one
        const label =
            items.length > 0 && section.label ? (
                <StyledSectionTitle key={sectionIndex + "-label"} role="presentation">
                    {translate(section.label)}
                </StyledSectionTitle>
            ) : (
                []
            );

        return [label, items];
    });

    return (
        <>
            <StyledMenu
                shortScreen={shortScreen}
                veryShortScreen={veryShortScreen}
                hideIllustration={hideIllustration}
                autoMinHeight={autoMinHeight}
                {...rest}
            >
                {menItems}
                {!hideIllustration && <AddNewIllustration item={currentItem}></AddNewIllustration>}
            </StyledMenu>

            {/* the active component is being rendered outside the previous menuItems calculation since */}
            {/* the modal can't be closed if rendered inside a menu item and mui MenuItem can't be wrapped with <></> */}
            {openModal && (
                <CustomComponent
                    key={openModal.key}
                    component={openModal.component}
                    open={true}
                    onCancel={() => setOpenModal(null)}
                />
            )}
        </>
    );
}

export default AddNewMenu;
